<template>
  <b-container ref="container" fluid class="position-relative bg-white shadow px-3 pb-5 px-6-lg pb-2-lg w-lg-70">
    <div class="full-mask" v-if="mfsAdmin && !startedByAdmin && !adminEditing"></div>
    <div class="py-3 above-full-mask">
      <b-row>
        <b-col cols="12" lg="6">
          <h2 >Stumpage Information</h2>
          <router-link tag="a" :to="`/notification/${notification.id}`">
            Notification #{{notification.fonNumber}}
          </router-link>
        </b-col>
        <b-col>
          <ul ref="errors">
            <li v-for="error in errors" :key="error" class="text-danger">{{error}}</li>
          </ul>
        </b-col>
      </b-row>
    </div>
    <b-form class="pb-lg-0 pb-8">
      <ValidationObserver ref="observer" slim>
        <b-card>
          <b-form-row>
            <h5 class="col-12 text-primary font-weight-bold pb-2">
              Questionnaire
            </h5>
          </b-form-row>
          <ValidationProvider name="Price Type" rules="required" v-slot="{ errors }">
            <div role="group" class="form-row form-group justify-content-between">
              <label id="stumpagePriceTypeLabel" for="stumpagePriceType" class="col-12 col-lg-6 col-form-label">
                <span class="req-ind">*</span>Are you reporting actual or imputed stumpage
                prices?
                <FontAwesomeIcon
                  focusable="true"
                  tabindex="0"
                  class="mx-2 text-primary align-self-center"
                  icon="info-circle"
                  size="lg"
                  :id="`stumpagePriceType-popover`"
                />
                <b-popover
                  :target="`stumpagePriceType-popover`"
                  :triggers="['hover', 'focus']"
                  placement="right"
                  custom-class="popover-lg"
                >
                  <ol class="pl-3">
                    <li>
                      <strong>Actual stumpage price</strong> is the value paid by a contractor/buyer
                      to the landowner for some or all of the standing trees in the landowner's
                      woodlot or a designated harvest area. Stumpage prices are negotiable and
                      should be agreed upon in advance. Written (or occasionally verbal) contracts
                      then assure the contractor (i.e. purchaser of stumpage) of the right to
                      harvest and remove the trees, possibly with certain conditions relating to
                      method of payment, timing of harvest, necessary improvements, and protection
                      of other resources. Stumpage is often determined and paid based on measure, or
                      scale, of the harvested wood's volume and quality, by a state licensed wood
                      scaler or scaling facility.
                    </li>
                    <li>
                      <strong>Imputed stumpage price</strong> is the value of a tree derived by subtracting the cutting,
                      skidding, and trucking costs for the forest products from the mill-delivered price.
                    </li>
                  </ol>
                </b-popover>
              </label>
              <b-form-radio-group
                id="stumpagePriceType"
                name="stumpagePriceType"
                class="noselect"
                v-model="model.stumpagePriceTypeId"
                :aria-describedby="$errorsIdString(errors, 'stumpagePriceType')"
                aria-labelledby="stumpagePriceTypeLabel"
              >
                <b-form-radio
                  v-for="stumpagePriceType in stumpagePriceTypes"
                  :value="stumpagePriceType.id"
                  :key="stumpagePriceType.id"
                  :id="`stumpagePriceType-${stumpagePriceType.id}`"
                  class="mx-lg-3"
                >
                  {{stumpagePriceType.name}}
                </b-form-radio>
              </b-form-radio-group>
              <InputErrors
                :errors="errors"
                errorsIdPrefix="stumpagePriceType"
                class="d-block invalid-feedback"
              />
            </div>
          </ValidationProvider>
        </b-card>
        <b-card>
          <b-form-row>
            <h5 class="col-12 text-primary font-weight-bold pb-2">
              Stumpage Details
            </h5>
          </b-form-row>
          <p class="font-sm">
            Complete all applicable sections by adding rows per Product type, selecting
            Species/Group and filling out associated fields. Stumpage prices are the unit prices a
            landowner who is NOT physically harvesting themselves is paid for the products and
            species removed during the harvest. Stumpage prices are typically negotiated in advance.
            During the harvest, species, product, and volume information for each truckload received
            by a mill is typically provided by the logger or other buyer, along with the mill slips
            and payment to the landowner based upon the negotiated stumpage price. If you have any
            questions about this section of the Report, please contact MFS by email at
            <a href="mailto:forestinfo@maine.gov">
                forestinfo@maine.gov</a> or by phone at <a href="tel:207-287-2791">207-287-2791</a>.
          </p>
          <div v-for="productType in productTypes" :key="productType.id">
            <hr class="border-dark">
            <StumpageSection :productType="productType" :stumpages="model.stumpages" />
          </div>
        </b-card>
      </ValidationObserver>
    </b-form>
    <b-card v-if="adminEditing" class="mt-3">
      <b-form-group label="Editor's Comment" label-for="editorCommentStumpage">
        <b-form-textarea
          id="editorCommentStumpage"
          v-model="editorComment"
          placeholder="Add a comment about changes made to Stumpage..."
          rows="3"
          max-rows="6"
          maxlength="255"
        />
      </b-form-group>
    </b-card>
  </b-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import FormSubmit from '@/mixins/FormSubmit';
import LookupModules from '@/store/moduleConfig';
import StumpageSection from '@/components/landownerreport/StumpageSection.vue';
import Utilities from '@/mixins/Utilities';

export default {
  name: 'StumpageForm',
  mixins: [FormSubmit, Utilities],
  props: ['landownerReport'],
  components: {
    StumpageSection,
  },
  data() {
    let model = {
      stumpagePriceTypeId: null,
      stumpages: [],
    };
    model = this.mergeTargetProps(model, this.landownerReport);
    return {
      model,
    };
  },
  mounted() {
    if (this.mfsAdmin && !this.startedByAdmin && !this.adminEditing) {
      if (this.disableTabbing) {
        setTimeout(() => {
          this.disableTabbing(this.$refs.container);
        }, 1500);
      }
    }
  },
  created() {
    this.$store.dispatch(`${LookupModules.PRODUCT_TYPES.namespace}/fetchOptions`);
    this.$store.dispatch(`${LookupModules.STUMPAGE_PRICE_TYPES.namespace}/fetchOptions`);
  },
  watch: {
    landownerReport() {
      this.model = this.mergeTargetProps(this.model, this.landownerReport);
    },
    productTypes() {
      if (this.mfsAdmin && !this.startedByAdmin && !this.adminEditing) {
        if (this.disableTabbing) {
          setTimeout(() => {
            this.disableTabbing(this.$refs.container);
          }, 1500);
        }
      }
    },
  },
  computed: {
    ...mapState(LookupModules.PRODUCT_TYPES.namespace, {
      productTypes: state => state.options,
      productTypesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.STUMPAGE_PRICE_TYPES.namespace, {
      stumpagePriceTypes: state => state.options,
      stumpagePriceTypesLoading: state => state.loading,
    }),
    ...mapState('Notification', {
      notification: state => state.notification,
    }),
    ...mapState('LandownerReport', {
      errors: state => state.stumpageErrors,
    }),
    ...mapGetters('User', ['mfsAdmin']),
    ...mapGetters('LandownerReport', ['adminEditing']),
    editorComment: {
      get() {
        return this.$store.state.LandownerReport.editorComments.stumpage;
      },
      set(value) {
        this.$store.dispatch('LandownerReport/setEditorComment', { section: 'stumpage', comment: value });
      },
    },
    startedByAdmin() {
      return this.landownerReport.statusId === 3;
    },
  },
  methods: {
    submitError() {
      const scrollContainer = document.querySelector('.wizard-tab-content,.fullContent');
      this.$scrollTo(this.$refs.errors, {
        container: scrollContainer || 'body',
      });
    },
    async submitWrapper() {
      const action = this.adminEditing
        ? 'LandownerReport/adminUpdateStumpages'
        : 'LandownerReport/updateStumpages';
      return this.submit(
        this.$refs.observer,
        action,
        this.model,
      );
    },
  },
};
</script>
